import { useTranslation } from "react-i18next";

import { DismissibleBanner } from "@/components/shared/DismissibleBanner";
import { useAuth } from "@/contexts/AuthContext";
import { resolveAppStoreTarget } from "@/lib/app-store-target";

const BANNER_ID_APP_DOWNLOAD = "app-download";

/**
 * Offers the mobile app to people browsing the portal on a phone.
 *
 * WHY THIS EXISTS: before it, the apps were linked from exactly one place in
 * the whole codebase — the landing page footer — and production had 5 users
 * with the app installed across 9 devices. The portal, where every signed-in
 * user lives and where the checkout flow runs, never mentioned it.
 *
 * WHICH STORE, AND WHETHER TO SHOW AT ALL, lives in `lib/app-store-target.ts`
 * so it can be tested without dragging React and the auth context into the
 * spec. Notably it stays SILENT on iOS Safari, which already renders Apple's
 * own Smart App Banner from the meta tag in index.html.
 *
 * Dismissal is per user and persists, through the same mechanism the profile
 * banners use: someone who says no is not asked again on that device. The
 * banner id carries the platform, so a person who dismissed it on Android and
 * later signs in on an iPhone is asked once more there — a different device
 * genuinely is a different offer.
 */
export function AppDownloadBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();

  if (!user?.id) return null;

  const target =
    typeof navigator === "undefined"
      ? null
      : resolveAppStoreTarget(navigator.userAgent);
  if (target == null) return null;

  return (
    <DismissibleBanner
      id={`${BANNER_ID_APP_DOWNLOAD}.${target.platform}`}
      userId={user.id}
      message={t("header.appBanner.message")}
      ctaLabel={t("header.appBanner.cta")}
      ctaHref={target.href}
      variant="info"
    />
  );
}
